type DirtyChecker = () => boolean;
type LeavePromptHandler = (onConfirm: () => void) => void;

const dirtyCheckers = new Set<DirtyChecker>();
const dirtyListeners = new Set<() => void>();

let promptLeaveHandler: LeavePromptHandler | null = null;
let guardBypassDepth = 0;

export function registerDirtyChecker(checker: DirtyChecker): () => void {
  dirtyCheckers.add(checker);
  notifyDirtyChanged();
  return () => {
    dirtyCheckers.delete(checker);
    notifyDirtyChanged();
  };
}

export function hasUnsavedChanges(): boolean {
  for (const checker of dirtyCheckers) {
    if (checker()) return true;
  }
  return false;
}

export function subscribeDirtyChanges(listener: () => void): () => void {
  dirtyListeners.add(listener);
  return () => dirtyListeners.delete(listener);
}

export function notifyDirtyChanged(): void {
  for (const listener of dirtyListeners) {
    listener();
  }
}

export function setPromptLeaveHandler(handler: LeavePromptHandler | null): void {
  promptLeaveHandler = handler;
}

export function promptLeave(onConfirm: () => void): void {
  if (promptLeaveHandler) {
    promptLeaveHandler(onConfirm);
    return;
  }
  onConfirm();
}

export function isGuardBypassed(): boolean {
  return guardBypassDepth > 0;
}

export function runWithGuardBypass<T>(fn: () => T): T {
  guardBypassDepth += 1;
  let decremented = false;
  let succeeded = false;

  const decrement = () => {
    if (decremented) return;
    decremented = true;
    guardBypassDepth -= 1;
  };

  const cleanup = () => {
    clearTimeout(timeoutId);
    window.removeEventListener("popstate", onPopState);
    decrement();
  };

  const onPopState = () => {
    cleanup();
  };

  window.addEventListener("popstate", onPopState, { once: true });
  const timeoutId = setTimeout(() => {
    cleanup();
  }, 0);

  try {
    const result = fn();
    succeeded = true;
    return result;
  } finally {
    if (!succeeded) {
      cleanup();
    }
  }
}
